// Notification preferences, from the screen (PLAN-2026-09-26-bell B4,
// NOTIFICATIONS.md §4): the matrix with its defaults, my overrides, and a
// save that sends the whole thing.
//
// A SWITCH IS MY OVERRIDE WHEN I HAVE ONE AND THE MATRIX'S DEFAULT OTHERWISE
// (`GET /api/notifications/matrix`). Turning a switch back to its default
// removes the override rather than storing a copy of it, so a default the
// site changes later reaches everybody who never changed it.
//
// EACH CHANGE SAVES AT ONCE, the way a phone's settings do: there is no Save
// button to forget. A save that fails puts the switch back and says why.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NotificationMatrixView,
  NotificationPrefs,
  valueOf,
  type NotificationMatrixViewShape,
  type NotificationPrefsShape,
  type NotificationTypeId,
} from '@fsp/domain';
import { messageOf, send } from './api';
import { useRead } from './read';
import { useSession } from './session';

export type Channel = 'push' | 'email';

export interface PrefsScreen {
  matrix: NotificationMatrixViewShape | null;
  prefs: NotificationPrefsShape | null;
  state: 'loading' | 'ready' | 'failed';
  problem: string | null;
  /** The switch as it stands: my override, or the default. */
  channelOn: (type: NotificationTypeId, channel: Channel) => boolean;
  setChannel: (type: NotificationTypeId, channel: Channel, on: boolean) => Promise<string | null>;
  /** Anything else on the prefs, changed and saved whole. */
  change: (next: (p: NotificationPrefsShape) => NotificationPrefsShape) => Promise<string | null>;
}

export function usePrefsScreen(): PrefsScreen {
  const { session } = useSession();
  const matrix: NotificationMatrixViewShape | null = valueOf(
    useRead('/notifications/matrix', NotificationMatrixView).snapshot,
  );
  const { snapshot: read } = useRead('/me/notifications/prefs', NotificationPrefs, {
    key: session?.userId ?? '',
    pending: session === null,
  });
  const loaded: NotificationPrefsShape | null = valueOf(read);
  const [prefs, setPrefs] = useState<NotificationPrefsShape | null>(null);
  useEffect(() => {
    if (loaded !== null) setPrefs(loaded);
  }, [loaded]);

  const defaults = useCallback(
    (type: NotificationTypeId) =>
      matrix?.groups.flatMap((g) => g.rows).find((r) => r.type === type) ?? null,
    [matrix],
  );

  const channelOn = useCallback(
    (type: NotificationTypeId, channel: Channel) =>
      prefs?.rows[type]?.[channel] ?? defaults(type)?.[channel] ?? false,
    [prefs, defaults],
  );

  // SAVES ANSWER IN ANY ORDER, SO ONLY THE NEWEST MAY SET THE SCREEN (the
  // review of B4). Two switches flipped quickly are two whole-document PUTs,
  // the second carrying both changes; an older answer landing last would put
  // the screen back to one change, and the next save would send that.
  // `confirmed` is the last document the server took, newest first, and is
  // what a failed save falls back to — never a snapshot from before a later
  // save that worked.
  const sent = useRef(0);
  const confirmed = useRef<{ seq: number; prefs: NotificationPrefsShape } | null>(null);
  useEffect(() => {
    if (loaded !== null && confirmed.current === null)
      confirmed.current = { seq: 0, prefs: loaded };
  }, [loaded]);

  const change = useCallback(
    async (next: (p: NotificationPrefsShape) => NotificationPrefsShape) => {
      if (prefs === null) return 'Your settings have not loaded yet.';
      // THE ZONE IS NOT THE SCREEN'S TO WRITE: quiet hours keep the account's
      // own (`users.time_zone`), which the server reads, so a copy here would
      // only go stale (the review of B4).
      const wanted = next(prefs);
      const seq = ++sent.current;
      setPrefs(wanted);
      const reply = await send('PUT', '/me/notifications/prefs', wanted, NotificationPrefs);
      if (reply.status === 'ok') {
        if (confirmed.current === null || seq > confirmed.current.seq)
          confirmed.current = { seq, prefs: reply.data };
        if (seq === sent.current) setPrefs(reply.data);
        return null;
      }
      // A later save is on its way or has landed: it carries this change or
      // supersedes it, and its answer is the one to show.
      if (seq === sent.current && confirmed.current !== null) setPrefs(confirmed.current.prefs);
      return messageOf(reply, 'That did not save.');
    },
    [prefs],
  );

  const setChannel = useCallback(
    (type: NotificationTypeId, channel: Channel, on: boolean) =>
      change((p) => {
        const row = { ...(p.rows[type] ?? {}) };
        if (on === defaults(type)?.[channel]) delete row[channel];
        else row[channel] = on;
        const rows = { ...p.rows };
        if (Object.keys(row).length === 0) delete rows[type];
        else rows[type] = row;
        return { ...p, rows };
      }),
    [change, defaults],
  );

  const failed = read.state === 'failed' && prefs === null;
  return {
    matrix,
    prefs,
    state: matrix !== null && prefs !== null ? 'ready' : failed ? 'failed' : 'loading',
    problem: failed ? 'Your notification settings could not be read.' : null,
    channelOn,
    setChannel,
    change,
  };
}
